import { GridSortItem } from "@mui/x-data-grid";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import {
  ContactDictionaryControllerApi,
  GetAllContactDictionariesContactTypeEnum,
} from "../../../services";
import {
  DGT_Pagination,
  getConfigObject,
  getPageIndexWhileSearch,
  getPageSize,
  useBaseContent,
} from "../../../utils/utils";

interface Props {
  pagination: DGT_Pagination;
  gridSorting: GridSortItem[];
  active?: boolean;
  archive?: boolean;
  contactType?: GetAllContactDictionariesContactTypeEnum;
  searchString?: string;
  xTENANTID?: string;
}

export type OptionData = {
  name: string;
  uuid: string;
};

/**
 * @author [Rajkumar Rathod]
 * @description [This hook is used to fetch the contact dictionary list]
 *
 * @param {DGT_Pagination} pagination - The pagination object
 * @param {GridSortItem[]} gridSorting - The grid sorting object
 * @param {boolean} active - The active status
 * @param {boolean} archive - The archive status
 * @param {string} searchString - The search string
 * @param {GetAllContactDictionariesContactTypeEnum} contactType - The contact type
 * @param {string} xTENANTID - The xTENANTID
 * @returns {useBaseContent<OptionData>} - The contact dictionary list
 */
export const useContactDictionaryList = ({
  pagination,
  gridSorting,
  active,
  archive,
  searchString,
  contactType,
  xTENANTID,
}: Props): useBaseContent<OptionData> => {
  const prevPage = useRef<number>(pagination.page);

  const pageIndex = getPageIndexWhileSearch({
    pagination,
    searchKey: searchString,
    prevPage,
  });
  const pageSize = getPageSize(searchString, pagination);

  const sortDirect = gridSorting?.[0]?.sort ?? undefined;
  const sortBy = sortDirect ? "firstName" : undefined;

  return useQuery({
    queryKey: [
      "contact-dictionary-list",
      pageIndex,
      pageSize,
      gridSorting,
      active,
      archive,
      searchString,
      contactType,
      xTENANTID,
    ],
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled: !!xTENANTID,
    queryFn: async () =>
      await new ContactDictionaryControllerApi(getConfigObject())
        .getAllContactDictionaries(
          pageIndex,
          pageSize,
          sortBy,
          sortDirect,
          active,
          archive,
          searchString,
          contactType || undefined,
          xTENANTID
        )
        .then((res) => res?.data),
  });
};
